import { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { createTokenExpirationManager } from './tokenUtils'

export interface AuthInterceptorDependencies {
  /** Get the current access token (in-memory, cached in cookie) */
  getToken: () => string | undefined
  /** Store a new access token */
  setToken: (token: string) => void
  /** Clear the stored access token */
  deleteToken: () => void
}

export interface AuthInterceptorState {
  isRefreshing: boolean
  failedQueue: Array<{
    resolve: (value?: unknown) => void
    reject: (reason?: unknown) => void
    config: AxiosRequestConfig
  }>
}

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _skipAuthRefresh?: boolean
  _retry?: boolean
}

/**
 * Creates auth interceptor state manager
 */
export const createAuthInterceptorState = (): AuthInterceptorState => ({
  isRefreshing: false,
  failedQueue: [],
})

/**
 * Resolve or reject all queued requests after a token refresh attempt.
 */
export const processQueue = (state: AuthInterceptorState, error: unknown = null) => {
  state.failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  state.failedQueue = []
}

/**
 * Auth endpoints that must never trigger a token refresh.
 */
const AUTH_ENDPOINTS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/logout',
  '/auth/otp',
  // Public passkey login ceremony (options + verify). A 401 here means a
  // failed/expired ceremony — refreshing and retrying would replay the
  // already-consumed challenge.
  '/auth/passkeys/login',
] as const

export const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false
  return AUTH_ENDPOINTS.some((ep) => url.includes(ep))
}

/**
 * Extract the new access token from a refresh response.
 * Checks the Authorization header first, then falls back to the response body.
 */
const extractToken = (response: { headers?: Record<string, unknown>; data?: unknown }): string | undefined => {
  const fromHeader = (response.headers as { authorization?: string } | undefined)?.authorization
  if (fromHeader) return fromHeader

  const fromBody = (response.data as { access_token?: string } | undefined)?.access_token
  return fromBody || undefined
}

/**
 * Enqueue a request to wait for an in-progress refresh, then resume
 * with the new token applied.
 */
const waitForRefresh = (
  state: AuthInterceptorState,
  config: InternalAxiosRequestConfig,
  getToken: () => string | undefined
): Promise<InternalAxiosRequestConfig> =>
  new Promise((resolve, reject) => {
    state.failedQueue.push({
      resolve: () => {
        config.headers.Authorization = getToken()
        resolve(config)
      },
      reject,
      config,
    })
  })

/**
 * Setup auth interceptors on an Axios instance.
 *
 * Strategy:
 * - **Request interceptor**: if a valid token exists but expires within
 *   5 minutes, proactively refresh before sending the request.
 *   If no token exists, let the request go through without auth and
 *   rely on the response interceptor to handle 401.
 * - **Response interceptor**: on 401, attempt a single refresh and
 *   retry the original request. Concurrent requests queue behind the
 *   first refresh attempt.
 */
export const setupAuthInterceptors = (
  axiosInstance: AxiosInstance,
  deps: AuthInterceptorDependencies,
  tokenManager = createTokenExpirationManager()
) => {
  const state = createAuthInterceptorState()

  // Seed the token manager from the cached token (cookie) if available
  tokenManager.updateFromToken(deps.getToken())

  /**
   * Call /auth/refresh and store the new token.
   * Must only be called when `state.isRefreshing` has been set to `true`
   * by the caller — this function handles `finally` cleanup.
   *
   * @returns the new token string, or throws on failure.
   */
  const performRefresh = async (): Promise<string> => {
    try {
      const response = await axiosInstance.post('/auth/refresh', undefined, {
        _skipAuthRefresh: true,
        withCredentials: true,
      } as AuthRequestConfig)

      const newToken = extractToken(response)
      if (!newToken) {
        throw new Error('Refresh succeeded but no token was returned')
      }

      deps.setToken(newToken)
      tokenManager.updateFromToken(newToken)
      processQueue(state)
      return newToken
    } catch (error) {
      processQueue(state, error)
      deps.deleteToken()
      throw error
    } finally {
      state.isRefreshing = false
    }
  }

  // ── Request interceptor ────────────────────────────────────────────
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if ((config as AuthRequestConfig)._skipAuthRefresh || isAuthEndpoint(config.url)) {
        return config
      }

      const token = deps.getToken()

      // No token or already expired — try to refresh before sending the request.
      if (!token || tokenManager.isExpired()) {
        if (state.isRefreshing) {
          return waitForRefresh(state, config, deps.getToken)
        }

        state.isRefreshing = true
        try {
          // Quiet refresh: use validateStatus to prevent axios from
          // rejecting on non-2xx — a failed refresh here is expected
          // (e.g. user not logged in) and should not log console errors.
          const response = await axiosInstance.post('/auth/refresh', undefined, {
            _skipAuthRefresh: true,
            withCredentials: true,
            validateStatus: () => true,
          } as AuthRequestConfig)

          const newToken = response.status >= 200 && response.status < 300
            ? extractToken(response)
            : undefined

          if (newToken) {
            deps.setToken(newToken)
            tokenManager.updateFromToken(newToken)
            processQueue(state)
            config.headers.Authorization = newToken
          } else {
            processQueue(state, new Error('Refresh failed'))
            deps.deleteToken()
            // Mark as _retry so the response interceptor won't attempt
            // a second refresh on the inevitable 401.
            ;(config as AuthRequestConfig)._retry = true
          }
        } catch {
          processQueue(state, new Error('Refresh failed'))
          deps.deleteToken()
          ;(config as AuthRequestConfig)._retry = true
        } finally {
          state.isRefreshing = false
        }
        return config
      }

      // Token is still valid — attach it and proceed.
      if (!tokenManager.isExpiringSoon(300)) {
        config.headers.Authorization = token
        return config
      }

      // Token is expiring soon — refresh proactively.
      if (state.isRefreshing) {
        return waitForRefresh(state, config, deps.getToken)
      }

      state.isRefreshing = true
      await performRefresh()
      config.headers.Authorization = deps.getToken()
      return config
    },
    (error) => Promise.reject(error)
  )

  // ── Response interceptor ───────────────────────────────────────────
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as AuthRequestConfig | undefined

      if (!originalRequest || originalRequest._skipAuthRefresh) {
        return Promise.reject(error)
      }

      const is401 = error.response?.status === 401
      if (!is401 || originalRequest._retry || isAuthEndpoint(originalRequest.url)) {
        return Promise.reject(error)
      }

      // If another request already started a refresh, wait for it.
      if (state.isRefreshing) {
        return waitForRefresh(state, originalRequest, deps.getToken).then((cfg) =>
          axiosInstance(cfg)
        )
      }

      originalRequest._retry = true
      state.isRefreshing = true

      await performRefresh()
      originalRequest.headers.Authorization = deps.getToken()
      return axiosInstance(originalRequest)
    }
  )

  return {
    tokenManager,
    state,
  }
}
